import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Pagination,
  CircularProgress,
  Tooltip,
  Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import FastfoodIcon from '@mui/icons-material/Fastfood';

interface ProductMapping {
  id: number;
  gtsProductId: string;
  gtsProductName: string;
  gtsProductImageUrl?: string;
  gtsStockCode: string;
  gtsCategoryName?: string;
  haciogluStokKodu: number;
  haciogluStokAdi?: string;
  haciogluImageUrl?: string;
  createdAt: string;
}

interface Props {
  loading: boolean;
  mappings: ProductMapping[];
  onDeleteMapping: (mapping: ProductMapping) => void;
}

const HaciogluMappingList: React.FC<Props> = ({
  loading,
  mappings,
  onDeleteMapping
}) => {
  const [mappingSearch, setMappingSearch] = useState('');
  const [mappingPage, setMappingPage] = useState(1);
  const itemsPerPage = 10;

  const filteredMappings = mappings.filter(mapping =>
    (mapping.gtsProductName && mapping.gtsProductName.toLowerCase().includes(mappingSearch.toLowerCase())) ||
    (mapping.haciogluStokAdi && mapping.haciogluStokAdi.toLowerCase().includes(mappingSearch.toLowerCase())) ||
    mapping.haciogluStokKodu.toString().includes(mappingSearch)
  );

  const paginatedMappings = filteredMappings.slice((mappingPage - 1) * itemsPerPage, mappingPage * itemsPerPage);

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Existing Mappings ({filteredMappings.length})
        </Typography>
        <TextField
          size="small"
          placeholder="Search..."
          value={mappingSearch}
          onChange={(e) => setMappingSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : paginatedMappings.length === 0 ? (
        <Box textAlign="center" py={5}>
          <Typography variant="body1" color="text.secondary">
            {mappingSearch ? 'No search results found' : 'No mappings created yet'}
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>GTS Product</TableCell>
                  <TableCell>GTS Category</TableCell>
                  <TableCell>Hacioglu Product</TableCell>
                  <TableCell>Hacioglu Code</TableCell>
                  <TableCell>Mapping Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={mapping.gtsProductImageUrl}
                          alt={mapping.gtsProductName}
                          sx={{ width: 40, height: 40, bgcolor: '#e3f2fd' }}
                        >
                          <FastfoodIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                        </Avatar>
                        <Typography variant="body2">{mapping.gtsProductName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{mapping.gtsCategoryName || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{mapping.haciogluStokAdi || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell>{mapping.haciogluStokKodu}</TableCell>
                    <TableCell>{new Date(mapping.createdAt).toLocaleDateString('tr-TR')}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove Mapping">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDeleteMapping(mapping)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(filteredMappings.length / itemsPerPage)}
              page={mappingPage}
              onChange={(e, page) => setMappingPage(page)}
            />
          </Box>
        </>
      )}
    </Paper>
  );
};

export default HaciogluMappingList;

